/**
 * A photo's address to show: its signed URL when it has one, else a direct
 * address in the photo store built from its filename, else the placeholder.
 *
 * One function so every picture in the app falls back the same way. The
 * direct address is a fallback only; the signed URL the backend sends is the
 * one meant to be shown.
 *
 * Who uses it: the photo editing dialog (EditPhotoDialog). Most screens
 * show the API's signed links (signed_photo_url, cardUrl, thumbUrl)
 * directly.
 *
 * @module imageUtils
 */

import { debugLog } from '@/config/config';
import { growLimit } from '@/shared/lib/growSettings';
import { PHOTO_PLACEHOLDER_URL } from '@/config/tunables/shared';

/** Base interface for any photo from our backend: its file, and its signed address when sent. */
export interface BasePhoto {
  /** The stored file's name. */
  filename: string;
  /** The signed address the backend sent: the one to show when present. */
  signed_photo_url?: string;
}

/** A photo with its ids and dates, as older responses send it. */
export interface Photo extends BasePhoto {
  /** Its id, under either name. */
  id?: number;
  photo_id?: number;
  /** The day it was taken, as the API sends it. */
  date_taken?: string;
  /** When it was uploaded, as the API sends it. */
  date_uploaded?: string;
}

/**
 * Gets the display URL for a photo from our backend.
 * This is the centralized function for consuming photos from our backend.
 * It handles both signed URLs and fallback to direct URLs.
 *
 * @param photo - The photo, a bare filename (legacy), or nothing.
 * @param userId - Its owner, for a direct address.
 * @param objId - The plant or location it is on, for a direct address.
 * @param type - Whether it is a plant's or a location's.
 * @returns An address to show; the placeholder when none can be made.
 */
export const getPhotoUrl = (photo: BasePhoto | string | null | undefined, userId?: number, objId?: number | string, type: 'plant' | 'location' = 'plant'): string => {
  debugLog('ImageUtils', 'Getting photo URL:', {
    photo: typeof photo === 'object' ? {
      ...photo,
      hasSignedPhotoUrl: !!photo?.signed_photo_url
    } : photo,
    userId,
    objId,
    type
  });

  // Handle null/undefined input
  if (!photo) {
    debugLog('ImageUtils', 'No photo provided, returning placeholder');
    return PHOTO_PLACEHOLDER_URL;
  }

  // Handle string input (legacy filename-only input)
  if (typeof photo === 'string') {
    debugLog('ImageUtils', 'String input, constructing direct URL');
    if (!userId || !objId) {
      debugLog('ImageUtils', 'Missing userId or objId for filename-only input');
      return PHOTO_PLACEHOLDER_URL;
    }
    return constructDirectUrl(photo, userId, objId, type);
  }

  // Use signed URL if available
  if (photo.signed_photo_url) {
    debugLog('ImageUtils', 'Found signed URL:', { signedUrl: photo.signed_photo_url });
    return photo.signed_photo_url;
  }

  debugLog('ImageUtils', 'No signed URL found, falling back to direct URL');

  // Fall back to direct URL if we have the necessary info
  if (!userId || !objId) {
    debugLog('ImageUtils', 'Missing userId or objId for fallback URL');
    return PHOTO_PLACEHOLDER_URL;
  }

  return constructDirectUrl(photo.filename, userId, objId, type);
};

/**
 * A photo's direct address in the store (for fallback).
 *
 * @param filename - The stored file's name.
 * @param userId - Its owner.
 * @param objId - The plant or location, as a number or numeric string.
 * @param type - Whether it is a plant's or a location's.
 * @returns The address, or the placeholder when objId is not a number.
 */
const constructDirectUrl = (filename: string, userId: number, objId: number | string, type: 'plant' | 'location'): string => {
  const objIdNum = typeof objId === 'string' ? parseInt(objId, 10) : objId;

  if (isNaN(objIdNum)) {
    debugLog('ImageUtils', 'Invalid objId:', { objId, objIdNum });
    return PHOTO_PLACEHOLDER_URL;
  }

  // Where photos are served from is the server's (limits.SPACES_BASE_URL); before the app has heard it there is only the placeholder.
  const spaces = growLimit('SPACES_BASE_URL');
  if (!spaces) return PHOTO_PLACEHOLDER_URL;
  const cleanFilename = encodeURIComponent(filename);
  const finalUrl = `${spaces}/${type === 'plant' ? 'plants' : 'locations'}/${userId}/${objIdNum}/${cleanFilename}`;

  debugLog('ImageUtils', 'Constructed direct URL:', {
    cleanFilename,
    finalUrl,
    originalFilename: filename,
    type
  });

  return finalUrl;
};

/**
 * The old name, kept for backward compatibility. Nothing calls it now.
 *
 * @deprecated Use getPhotoUrl instead
 */
export const normalizeImageUrl = (input: string | Photo | null, userId?: number, objId?: number | string, type: 'plant' | 'location' = 'plant'): string => {
  debugLog('ImageUtils', 'WARNING: Using deprecated normalizeImageUrl, please migrate to getPhotoUrl');
  return getPhotoUrl(input, userId, objId, type);
}; 